import { ChildUser, UserRole } from "@joonapp/web-shared"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import UserSelectHeaderButton from "./UserSelectHeaderButton"
import useMobile from "../../hooks/useMobile"
import { useFamilyQuery, useUserQuery } from "../../networking/queries"
import { UserSelectFilterType } from "../../types"
import { getChildImage } from "../../util/util"

interface UserSelectProps {
  selectedUserId: number | null
  onUserSelect: (
    selectedFilterType: UserSelectFilterType,
    userId: number | null
  ) => void
  selectedFilterType: UserSelectFilterType
  style?: React.CSSProperties

  showAllOption?: boolean
  showAllKidsOption?: boolean
  showMeOption?: boolean
  showCoparents?: boolean
  pausedUserIds?: number[]
  restrictedScreenTimeUserIds?: number[]
  notificationUserIds?: number[]
  showAllKidsOptionAlways?: boolean
}

const UserSelect = ({
  selectedUserId,
  onUserSelect,
  selectedFilterType,
  showAllOption = true,
  showAllKidsOption = true,
  showAllKidsOptionAlways = false,
  showMeOption = true,
  showCoparents = true,
  pausedUserIds = [],
  restrictedScreenTimeUserIds = [],
  notificationUserIds = [],
}: UserSelectProps) => {
  const { user } = useUserQuery()
  const { data: family } = useFamilyQuery()
  const isMobile = useMobile()
  const { t } = useTranslation()

  const userSelectOptions = useMemo(
    () => [
      ...(showAllOption
        ? [
            {
              label: t("USER_SELECT_ALL"),
              filterType: UserSelectFilterType.ALL_USERS,
              imageUrl: null,
              parentAvatar: null,
              userId: null,
            },
          ]
        : []),
      ...(showAllKidsOptionAlways ||
      (showAllKidsOption && family?.profiles && family?.profiles?.length > 2)
        ? [
            {
              label: t("USER_SELECT_ALL_KIDS"),
              filterType: UserSelectFilterType.ALL_KIDS,
              imageUrl: null,
              parentAvatar: null,
              userId: null,
            },
          ]
        : []),
      ...(showMeOption
        ? [
            {
              label: t("NAV_ME"),
              filterType: UserSelectFilterType.USER,
              imageUrl: null,
              parentAvatar: user?.parent_doter.type ?? null,
              userId: user?.id ?? null,
            },
          ]
        : []),
      ...(family?.profiles
        ?.filter((profile) => {
          const isCoparent =
            profile.user.id !== user?.id &&
            profile.role === UserRole.PARENT &&
            showCoparents
          return profile.role === UserRole.CHILD || isCoparent
        })
        .map((profile) => ({
          label: profile.user.name,
          filterType: UserSelectFilterType.USER,
          imageUrl:
            profile.role === UserRole.PARENT
              ? null
              : getChildImage(profile.user as ChildUser),
          parentAvatar:
            profile.role === UserRole.PARENT
              ? profile.user.parent_doter.type
              : null,
          userId: profile.user.id,
        })) || []),
    ],
    [
      family,
      user,
      showAllOption,
      showAllKidsOption,
      showMeOption,
      showCoparents,
      showAllKidsOptionAlways,
      t,
    ]
  )

  const isSelected = (
    userId: number | null,
    selectOption: UserSelectFilterType
  ) => {
    switch (selectOption) {
      case UserSelectFilterType.ALL_USERS:
      case UserSelectFilterType.ALL_KIDS:
        return selectOption === selectedFilterType
      case UserSelectFilterType.USER:
        return userId === selectedUserId
    }
  }

  return (
    <div
      className={`flex flex-row items-center justify-start w-full max-w-[600px] gap-1 overflow-x-auto ${
        isMobile ? "px-4" : "px-0"
      }`}
      style={{ scrollbarWidth: "none" }}
    >
      {userSelectOptions.map(
        ({ label, filterType, imageUrl, parentAvatar, userId }) => (
          <UserSelectHeaderButton
            key={`${userId || ""}-${label}`}
            label={label}
            imageUrl={imageUrl}
            parentAvatar={parentAvatar}
            filterType={filterType}
            onClick={() => onUserSelect(filterType, userId)}
            isSelected={isSelected(userId, filterType)}
            isPaused={userId ? pausedUserIds.includes(userId) : false}
            isRestrictedScreenTime={
              userId ? restrictedScreenTimeUserIds.includes(userId) : false
            }
            hasNotification={
              userId ? notificationUserIds.includes(userId) : false
            }
          />
        )
      )}
    </div>
  )
}

export default UserSelect
